import PropTypes from 'prop-types';
import React from 'react';
import { ComposedChart, ResponsiveContainer, XAxis, YAxis, ReferenceLine } from 'recharts';
import moment from 'moment-timezone';
import Slider from '@mui/material/Slider';
import { ThemeProvider } from '@mui/material/styles';
import { cloneDeep } from 'lodash';

import { RANGE_PICKER_HEIGHT } from '../GraphConstants';
import { DATE_MONTH_DAY_YEAR } from '../../../constants';
import { getColorsForColorTheme } from '../../../utils/colorTheme';
import { drawLineSeries } from './DrawMetrics';
import newTheme from '../../../style/muiTheme';

const TRAVELLER_WIDTH = 8;
const TRACK_HEIGHT = 30;
export const RESOLUTION_DAY = 60 * 60 * 24; // 1 day in seconds

export const RangePicker = React.forwardRef((props, ref) => {
  const { maxDateRange, startTimestamp, endTimestamp, onChange } = props;
  const min = maxDateRange.start.unix();
  const max = maxDateRange.end.unix();
  const { maxDistanceDays = max-min, minDistanceDays = 0, leftPadding, rightPadding } = props;
  const [value, setValue] = React.useState([startTimestamp, endTimestamp]);
  const minDistance = minDistanceDays * RESOLUTION_DAY;
  const maxDistance = maxDistanceDays * RESOLUTION_DAY;

  const [elementDragged, setElementDragged] = React.useState();
  const startPosition = React.useRef(0)
  const rangeRef = React.useRef([startTimestamp, endTimestamp]);

  const height = RANGE_PICKER_HEIGHT;
  const theme = React.useMemo(() => {
    const ret = cloneDeep(newTheme(getColorsForColorTheme().mainColor));
    ret.components.MuiSlider.styleOverrides.root = {
      padding: '0',
      height: TRACK_HEIGHT,
      borderRadius: TRACK_HEIGHT / 2,
      boxShadow: 'inset 0 3px 5px 0 rgba(0, 0, 0, 0.22)',
      border: '1px solid #bababa',
    };
    ret.components.MuiSlider.styleOverrides.thumb = {
      width: TRAVELLER_WIDTH,
      height,
      '&::before': {
        width: TRAVELLER_WIDTH,
        height,
        backgroundColor: getColorsForColorTheme().mainColor,
        borderRadius: TRAVELLER_WIDTH,
      },
    };
    ret.components.MuiSlider.styleOverrides.track = {
      opacity: 0.3,
      borderRadius: 0,
      height: TRACK_HEIGHT,
      backgroundColor: getColorsForColorTheme().mainColor,
      cursor: 'ew-resize'
    };

    return ret;
  }, []);

  React.useEffect(() => {
    if (startTimestamp === value[0] && endTimestamp === value[1]) {
      return;
    }

    setValue([startTimestamp, endTimestamp]);
  }, [startTimestamp, endTimestamp]);
  
  const handleChange = (event, newValue, activeThumb) => {
    if (activeThumb === 0) {
      setValue([Math.max(Math.min(newValue[0], value[1] - minDistance), value[1] - maxDistance), value[1]]);
      onChange(Math.max(Math.min(newValue[0], value[1] - minDistance), value[1] - maxDistance), value[1]);
    } else {
      setValue([value[0], Math.min(Math.max(newValue[1], value[0] + minDistance), value[0] + maxDistance)]);
      onChange(value[0], Math.min(Math.max(newValue[1], value[0] + minDistance), value[0] + maxDistance));
    }
  };

  const handlePointerMove = (event) => {
    if (elementDragged === 'track') {
      const currentDistance = value[1] - value[0];
      const diff = event.clientX - startPosition.current;
      const scale = (max - min) / event.currentTarget.getBoundingClientRect().width;
      const delta = diff * scale;
      const newValue = rangeRef.current.map((val) => Math.max(min, Math.min(max, val + delta)));
      const newValueRounded = newValue.map((val) => Math.round(val / RESOLUTION_DAY) * RESOLUTION_DAY);
      if (diff < 0) {
        setValue([newValueRounded[0], Math.max(newValueRounded[1], newValueRounded[0] + currentDistance)]);
        onChange(newValueRounded[0], Math.max(newValueRounded[1], newValueRounded[0] + currentDistance));
      } else {
        setValue([Math.min(newValueRounded[0], newValueRounded[1] - currentDistance), newValueRounded[1]]);
        onChange(Math.min(newValueRounded[0], newValueRounded[1] - currentDistance), newValueRounded[1]);
      }
    }
  };

  const onPointerDown = (e) => {
    if (e?.target?.className?.includes('MuiSlider-thumb')) {
      setElementDragged('thumb');
    } else if (e?.target?.className?.includes('MuiSlider-track')) {
      setElementDragged('track');
      e.target.setPointerCapture(e.pointerId);
      startPosition.current = e.clientX;
      rangeRef.current = value;
    } else {
      setElementDragged(undefined);
    }
  };

  return (
    <div
      style={{
        width: props.width || '100%',
        height,
        paddingLeft: leftPadding || 0, 
        paddingRight: rightPadding || 0,
        paddingTop: (height - TRACK_HEIGHT) / 2,
        paddingBottom: (height - TRACK_HEIGHT) / 2,
      }}
      ref={ref}
    >
      <ThemeProvider theme={theme}>
        <Slider
          value={value}
          min={min}
          max={max - RESOLUTION_DAY}
          step={RESOLUTION_DAY}
          onChange={(e, v, t) => elementDragged === 'thumb' ? handleChange(e, v, t) : undefined}
          disableSwap
          valueLabelDisplay="auto"
          valueLabelFormat={v => {return moment(v * 1000).format(DATE_MONTH_DAY_YEAR);}}
          slots={{ rail: InnerChart }}
          slotProps={props}
          onPointerDown={onPointerDown}
          onPointerUp={() => setElementDragged(undefined)}
          onPointerMove={handlePointerMove}
          classes={elementDragged === 'track' ? { root: 'brushing', thumb: 'Mui-active', valueLabel: 'MuiSlider-valueLabelOpen' } : {}}
        />
      </ThemeProvider>
    </div>
  );
});

RangePicker.propTypes = {
  startTimestamp: PropTypes.number,
  endTimestamp: PropTypes.number,
  onChange: PropTypes.func,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  leftPadding: PropTypes.number,
  rightPadding: PropTypes.number,
  maxDateRange: PropTypes.shape({
    start: PropTypes.any,
    end: PropTypes.any,
  }),
  maxDistanceDays: PropTypes.number,
  minDistanceDays: PropTypes.number,
};

const InnerChart = React.forwardRef((props, ref) => {
  const { ownerState } = props;
  const { slotProps } = ownerState;
  const { maxDateRange, height, width, metric, bottomMargin, dateEntries, yAxisGroups, data, tooltipX } = slotProps;
  const yAxis = yAxisGroups?.find(g => g.metricsExt.some(m => m.id === metric));
  const domain = yAxis?.yMax && yAxis?.yMin ? [yAxis.yMin, yAxis.yMax] : ['auto', 'auto'];

  return (
    <ResponsiveContainer width={width} height={height} className="brush" ref={ref}>
      <ComposedChart
        margin={{
          top: 0,
          right: 0,
          bottom: bottomMargin,
          left: 0,
        }}
        data={dateEntries}
      >
        <XAxis
          dataKey="x"
          type="number"
          domain={[maxDateRange.start.unix(), maxDateRange.end.unix()]}
          hide
        />
        <YAxis
          domain={domain}
          hide
          yAxisId="yAxis1"
          padding={{ top: TRAVELLER_WIDTH, bottom: TRAVELLER_WIDTH }}
        />
        {metric && drawLineSeries([metric], yAxisGroups, data, maxDateRange, null, false, true)}
        {tooltipX && (
          <ReferenceLine
            x={tooltipX}
            yAxisId="yAxis1"
            stroke={getColorsForColorTheme().mainColor}
            strokeWidth={2}
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
)});

InnerChart.propTypes = {
  ownerState: PropTypes.shape({
    slotProps: PropTypes.shape({
      maxDateRange: PropTypes.shape({
        start: PropTypes.any,
        end: PropTypes.any,
      }),
      height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      metric: PropTypes.string,
      bottomMargin: PropTypes.number,
      dateEntries: PropTypes.arrayOf(PropTypes.object),
      yAxisGroups: PropTypes.arrayOf(PropTypes.object),
      data: PropTypes.arrayOf(PropTypes.object),
      tooltipX: PropTypes.number,
    }),
  }),
};
